import React, { useState, useMemo } from 'react';
import {
  Shield,
  ShieldAlert,
  ShieldCheck,
  AlertTriangle,
  AlertCircle,
  Clock,
  CheckCircle2,
  XCircle,
  FileText,
  Upload,
  Download,
  Filter,
  Search,
  ChevronRight,
  ChevronDown,
  ExternalLink,
  Info,
  RefreshCw,
  Sliders,
  Users,
  Layers,
  TrendingUp,
  DollarSign,
  Database,
  Building2,
  Globe,
  Lock,
  Eye,
  Check,
  X,
  FileCheck,
  Plus,
  HelpCircle,
  Bell,
  Sparkles,
  ArrowUpRight
} from 'lucide-react';
import { AppView } from '../../types';

interface ComplianceMonitorProps {
  onNavigateToView: (view: AppView) => void;
  onOpenSearch?: () => void;
}

// Canonical applicability states as mandated
export type ApplicabilityState =
  | 'Applicable'
  | 'Likely Applicable'
  | 'Potentially Applicable'
  | 'Review Required'
  | 'Not Applicable';

// Control statuses as mandated
export type ControlStatus =
  | 'Operating'
  | 'Needs Attention'
  | 'Not Tested'
  | 'Not Applicable'
  | 'Pending Review';

// Evidence statuses as mandated
export type EvidenceStatus = 'Complete' | 'Partial' | 'Missing' | 'Expired';

export interface ComplianceControlItem {
  id: string;
  code: string;
  title: string;
  description: string;
  framework: 'PCI-DSS v4.0' | 'GDPR' | 'PSD2' | 'SOC 2 Type II' | 'ISO27001' | 'DORA';
  jurisdiction: string;
  issue: string;
  owner: string;
  ownerEmail: string;
  due: string;
  evidence: EvidenceStatus;
  evidenceFilesCount: number;
  status: ControlStatus;
  lastTestedDate: string;
  remediationPlan?: string;
}

export interface FrameworkApplicabilityItem {
  id: string;
  framework: string;
  fullName: string;
  region: string;
  status: ApplicabilityState;
  justification: string;
  controlCount: number;
  lastReviewDate: string;
  nextReviewDate: string;
}

export const ComplianceMonitor: React.FC<ComplianceMonitorProps> = ({
  onNavigateToView,
  onOpenSearch
}) => {
  const [selectedDomain, setSelectedDomain] = useState<string>('operations');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedFrameworkFilter, setSelectedFrameworkFilter] = useState<string>('ALL');
  const [selectedEvidenceFilter, setSelectedEvidenceFilter] = useState<string>('ALL');
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Detail & Action Modals
  const [selectedControl, setSelectedControl] = useState<ComplianceControlItem | null>(null);
  const [showUploadEvidenceModal, setShowUploadEvidenceModal] = useState<boolean>(false);
  const [uploadTargetControl, setUploadTargetControl] = useState<ComplianceControlItem | null>(null);
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState<boolean>(false);

  const triggerToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  // 1. Framework Applicability Registry (No fake compliance % scores, strictly verified applicability states)
  const [frameworks, setFrameworks] = useState<FrameworkApplicabilityItem[]>([
    {
      id: 'fw-pci',
      framework: 'PCI-DSS v4.0',
      fullName: 'Payment Card Industry Data Security Standard v4.0',
      region: 'Global',
      status: 'Review Required',
      justification: 'Scope boundary validation underway for direct cardholder data environment (CDE) tokenization proxy.',
      controlCount: 48,
      lastReviewDate: '2026-07-15',
      nextReviewDate: '2026-10-15'
    },
    {
      id: 'fw-gdpr',
      framework: 'GDPR',
      fullName: 'General Data Protection Regulation (EU 2016/679) & UK GDPR',
      region: 'EU / UK',
      status: 'Review Required',
      justification: 'Cross-border telemetry data transfer and Article 30 Records of Processing Activities (ROPA) update pending.',
      controlCount: 54,
      lastReviewDate: '2026-08-01',
      nextReviewDate: '2026-11-01'
    },
    {
      id: 'fw-psd2',
      framework: 'PSD2',
      fullName: 'Payment Services Directive 2 / Strong Customer Authentication (SCA)',
      region: 'EU',
      status: 'Review Required',
      justification: 'Exemption engine audit underway for dynamic linking and low-value transaction risk thresholds.',
      controlCount: 32,
      lastReviewDate: '2026-06-20',
      nextReviewDate: '2026-09-20'
    },
    {
      id: 'fw-soc2',
      framework: 'SOC 2 Type II',
      fullName: 'AICPA Trust Services Criteria (Security, Availability, Confidentiality)',
      region: 'US',
      status: 'Potentially Applicable',
      justification: 'Enterprise US customers requesting SOC 2 Type II scoping. Readiness gap assessment initialized; no public attestation published.',
      controlCount: 39,
      lastReviewDate: '2026-08-10',
      nextReviewDate: '2026-12-01'
    }
  ]);

  // 2. Controls Requiring Attention (Realistic compliance findings matching the screenshot)
  const [controls, setControls] = useState<ComplianceControlItem[]>([
    {
      id: 'ctrl-1',
      code: 'AC-03: Logical Access Review',
      title: 'Privileged Role & Admin Access Recertification',
      description: 'Quarterly recertification of all root, super admin, and bastion jump host permissions against HR status.',
      framework: 'PCI-DSS v4.0',
      jurisdiction: 'PCI-DSS (Global)',
      issue: 'Quarterly access review incomplete for DevOps production bastion users',
      owner: 'S. Connor',
      ownerEmail: 's.connor@yamoopay.com',
      due: '2026-09-15',
      evidence: 'Partial',
      evidenceFilesCount: 2,
      status: 'Needs Attention',
      lastTestedDate: '2026-07-28',
      remediationPlan: 'Audit log extraction scheduled for all SSH bastion key rotations; awaiting IAM sign-off.'
    },
    {
      id: 'ctrl-2',
      code: 'DP-01: Data Retention Lifecycle',
      title: 'Automated PII Deletion & Purging Engine',
      description: 'Enforce strict 90-day retention cutoff for non-settled checkout sessions and unauthenticated calculation payloads.',
      framework: 'GDPR',
      jurisdiction: 'GDPR (EU)',
      issue: 'Automated deletion script failed for staging customer mock logs',
      owner: 'M. Vance',
      ownerEmail: 'm.vance@yamoopay.com',
      due: '2026-08-30',
      evidence: 'Missing',
      evidenceFilesCount: 0,
      status: 'Needs Attention',
      lastTestedDate: '2026-08-14',
      remediationPlan: 'Deploy hotfix for GCS object lifecycle policy and verify automated tombstone records.'
    },
    {
      id: 'ctrl-3',
      code: 'IR-02: Incident Response Exercise',
      title: 'Annual Disaster & Breach Tabletop Simulation',
      description: 'Execute cross-functional security incident simulation with executive leadership and external legal counsel.',
      framework: 'ISO27001',
      jurisdiction: 'ISO27001',
      issue: 'Annual tabletop exercise report pending CISO final signature',
      owner: 'A. Chen',
      ownerEmail: 'a.chen@yamoopay.com',
      due: '2026-09-01',
      evidence: 'Complete',
      evidenceFilesCount: 4,
      status: 'Needs Attention',
      lastTestedDate: '2026-08-20',
      remediationPlan: 'Report compiled and validated by third-party auditor; routed to DocuSign queue.'
    },
    {
      id: 'ctrl-4',
      code: 'SCA-04: Dynamic Linking Exemption',
      title: 'Strong Customer Authentication Audit Trail',
      description: 'Cryptographic binding of transaction amount and payee identity for all frictionless checkout exemptions.',
      framework: 'PSD2',
      jurisdiction: 'PSD2 (EU)',
      issue: 'Dynamic linking signature log rotation requires secondary checksum verification',
      owner: 'K. Tanaka',
      ownerEmail: 'k.tanaka@yamoopay.com',
      due: '2026-09-10',
      evidence: 'Partial',
      evidenceFilesCount: 1,
      status: 'Needs Attention',
      lastTestedDate: '2026-08-18',
      remediationPlan: 'Add HMAC SHA-256 verification hash to transaction receipt pipeline.'
    },
    {
      id: 'ctrl-5',
      code: 'CC-6.8: Vulnerability Remediation',
      title: 'Critical CVE SLA Remediation Window',
      description: 'Enforce 14-day patching SLA on all container base images with CVSS score > 7.0.',
      framework: 'SOC 2 Type II',
      jurisdiction: 'SOC 2 (US)',
      issue: '2 dependency packages flagged with medium CVE awaiting upstream patch',
      owner: 'J. Doe',
      ownerEmail: 'j.doe@yamoopay.com',
      due: '2026-09-05',
      evidence: 'Missing',
      evidenceFilesCount: 0,
      status: 'Needs Attention',
      lastTestedDate: '2026-08-22',
      remediationPlan: 'Bump base Alpine image in Dockerfile to 3.20.2 in next maintenance release.'
    }
  ]);

  // Filtered Controls
  const filteredControls = useMemo(() => {
    return controls.filter((ctrl) => {
      const matchesSearch =
        searchQuery === '' ||
        ctrl.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ctrl.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ctrl.issue.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ctrl.owner.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ctrl.jurisdiction.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesFramework =
        selectedFrameworkFilter === 'ALL' || ctrl.framework.includes(selectedFrameworkFilter);

      const matchesEvidence =
        selectedEvidenceFilter === 'ALL' || ctrl.evidence === selectedEvidenceFilter;

      return matchesSearch && matchesFramework && matchesEvidence;
    });
  }, [controls, searchQuery, selectedFrameworkFilter, selectedEvidenceFilter]);

  // Counts (Exact match to Stitch screenshot: 12 Controls Requiring Attention, 5 Missing Evidence, 4 Frameworks Tracked)
  const controlsRequiringAttentionCount = 12;
  const missingEvidenceCount = 5;
  const frameworksTrackedCount = 4;

  // Control Status Breakdown (Exact match to screenshot bottom right):
  // Operating as Intended: 142
  // Needs Attention: 12
  // Not Evaluated / Exempt: 19
  // Total = 173
  const operatingCount = 142;
  const needsAttentionCount = 12;
  const notEvaluatedCount = 19;
  const totalControls = operatingCount + needsAttentionCount + notEvaluatedCount; // 173

  const operatingPercent = ((operatingCount / totalControls) * 100).toFixed(0);
  const needsAttentionPercent = ((needsAttentionCount / totalControls) * 100).toFixed(0);
  const notEvaluatedPercent = ((notEvaluatedCount / totalControls) * 100).toFixed(0);

  // Evidence Status Styling Helper
  const renderEvidenceBadge = (status: EvidenceStatus) => {
    switch (status) {
      case 'Complete':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#DCFCE7] text-[#16A34A]">
            <Check className="w-3.5 h-3.5" />
            <span>Complete</span>
          </span>
        );
      case 'Partial':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FEE2E2] text-[#DC2626]">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>Partial</span>
          </span>
        );
      case 'Missing':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FEE2E2] text-[#DC2626]">
            <X className="w-3.5 h-3.5" />
            <span>Missing</span>
          </span>
        );
      case 'Expired':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FEF3C7] text-[#D97706]">
            <Clock className="w-3.5 h-3.5" />
            <span>Expired</span>
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            {status}
          </span>
        );
    }
  };

  // Status Icon Helper
  const renderStatusIcon = (status: ControlStatus, evidence: EvidenceStatus) => {
    if (status === 'Needs Attention') {
      if (evidence === 'Complete') {
        return (
          <div className="w-5 h-5 rounded-full flex items-center justify-center text-[#D97706]" title="Pending Review / Sign-off">
            <Clock className="w-4 h-4" />
          </div>
        );
      }
      return (
        <div className="w-5 h-5 rounded-full flex items-center justify-center text-[#DC2626]" title="Action Required">
          <AlertCircle className="w-4 h-4" />
        </div>
      );
    }
    return (
      <div className="w-5 h-5 rounded-full flex items-center justify-center text-[#16A34A]">
        <CheckCircle2 className="w-4 h-4" />
      </div>
    );
  };

  // Applicability Status Styling Helper
  const renderApplicabilityStatus = (status: ApplicabilityState) => {
    switch (status) {
      case 'Review Required':
        return <span className="font-bold text-[#0D9488]">{status}</span>;
      case 'Potentially Applicable':
        return <span className="font-bold text-[#D97706]">{status}</span>;
      case 'Applicable':
        return <span className="font-bold text-[#059669]">{status}</span>;
      case 'Likely Applicable':
        return <span className="font-bold text-[#2563EB]">{status}</span>;
      case 'Not Applicable':
        return <span className="font-bold text-[#64748B]">{status}</span>;
      default:
        return <span className="font-bold text-[#1E293B]">{status}</span>;
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex text-[#1E293B] font-sans antialiased">
      {/* =============================================================== */}
      {/* 1. LEFT SIDEBAR (Canonical 7 Domains, with Operations active)   */}
      {/* =============================================================== */}
      <aside className="w-64 bg-white border-r border-[#E2E8F0] flex flex-col shrink-0 min-h-screen sticky top-0 h-screen overflow-y-auto">
        {/* Brand Header */}
        <div className="p-5 border-b border-[#F1F5F9] flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#0F4F4F] flex items-center justify-center text-white shadow-xs font-black text-lg">
            <span className="font-black text-white text-base leading-none">Y</span>
          </div>
          <div>
            <h1 className="text-base font-extrabold text-[#0F172A] tracking-tight leading-none">
              Control Center
            </h1>
            <span className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider block mt-1">
              Administrator
            </span>
          </div>
        </div>

        {/* Sidebar Navigation Links */}
        <div className="flex-1 py-4 px-3 space-y-1">
          <button
            onClick={() => {
              setSelectedDomain('overview');
              onNavigateToView('admin-super-command-center');
            }}
            className="w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <Layers className="w-4 h-4" />
            <span>Overview</span>
          </button>

          <button
            onClick={() => {
              setSelectedDomain('customers');
              onNavigateToView('admin-organizations');
            }}
            className="w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <Users className="w-4 h-4" />
            <span>Customers & Support</span>
          </button>

          <button
            onClick={() => {
              setSelectedDomain('product');
              onNavigateToView('cpo-command-center');
            }}
            className="w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <TrendingUp className="w-4 h-4" />
            <span>Product & Growth</span>
          </button>

          <button
            onClick={() => {
              setSelectedDomain('revenue');
              onNavigateToView('cfo-command-center');
            }}
            className="w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <DollarSign className="w-4 h-4" />
            <span>Revenue & Finance</span>
          </button>

          <button
            onClick={() => {
              setSelectedDomain('data');
              onNavigateToView('admin-ai-platform');
            }}
            className="w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <Database className="w-4 h-4" />
            <span>Data & AI</span>
          </button>

          {/* ACTIVE: Operations & Risk (Minty/emerald background matching screenshot) */}
          <div>
            <button
              onClick={() => setSelectedDomain('operations')}
              className="w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold bg-[#E6F7F0] text-[#0E7044] transition-all cursor-pointer"
            >
              <ShieldCheck className="w-4 h-4 text-[#0E7044]" />
              <span>Operations & Risk</span>
            </button>

            {/* Sub-menu under Operations & Risk */}
            <div className="ml-4 pl-3 border-l-2 border-[#0E7044] my-1 space-y-1">
              <button
                className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-bold text-[#0E7044] bg-[#E6F7F0]/60 transition-colors cursor-pointer"
              >
                Compliance Monitor
              </button>
              <button
                onClick={() => onNavigateToView('admin-system-health')}
                className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer"
              >
                System Health
              </button>
              <button
                onClick={() => onNavigateToView('ciso-security-command-center')}
                className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer"
              >
                CISO Security
              </button>
              <button
                onClick={() => onNavigateToView('admin-audit')}
                className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer"
              >
                Audit Logs
              </button>
              <button
                onClick={() => onNavigateToView('admin-reports')}
                className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer"
              >
                Audit & Reports
              </button>
            </div>
          </div>

          <button
            onClick={() => {
              setSelectedDomain('configuration');
              onNavigateToView('admin-roles-permissions');
            }}
            className="w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <Sliders className="w-4 h-4" />
            <span>Configuration</span>
          </button>
        </div>

        {/* Sidebar Footer Profile */}
        <div className="p-3 border-t border-[#F1F5F9]">
          <div className="flex items-center gap-3 p-2 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
            <div className="w-8 h-8 rounded-full bg-[#0F4F4F] text-white font-bold text-xs flex items-center justify-center">
              AD
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-xs font-bold text-[#0F172A] block truncate">Compliance Officer</span>
              <span className="text-[10px] font-mono text-[#64748B] block truncate">Auditor Level 3</span>
            </div>
          </div>
        </div>
      </aside>

      {/* =============================================================== */}
      {/* 2. MAIN CONTENT AREA                                           */}
      {/* =============================================================== */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header Bar */}
        <header className="h-16 bg-white border-b border-[#E2E8F0] px-8 flex items-center justify-between sticky top-0 z-20">
          <div>
            <h1 className="text-lg font-bold text-[#0F172A] tracking-tight">
              Compliance Monitor
            </h1>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => triggerToast('System Health: 100% of cryptographic audit logs validated.')}
              className="text-[#64748B] hover:text-[#0F172A] p-2 rounded-lg hover:bg-[#F1F5F9] transition-colors cursor-pointer"
              title="Audit Log Integrity"
            >
              <AlertTriangle className="w-4 h-4" />
            </button>
            <button
              onClick={() => triggerToast('Audit Checklist: 4 Frameworks active in current fiscal cycle.')}
              className="text-[#64748B] hover:text-[#0F172A] p-2 rounded-lg hover:bg-[#F1F5F9] transition-colors cursor-pointer"
              title="Audit Checklist"
            >
              <FileCheck className="w-4 h-4" />
            </button>
            <button
              onClick={() => triggerToast('No unread compliance escalation notifications.')}
              className="text-[#64748B] hover:text-[#0F172A] p-2 rounded-lg hover:bg-[#F1F5F9] transition-colors cursor-pointer"
              title="Notifications"
            >
              <Bell className="w-4 h-4" />
            </button>
            <div className="w-8 h-8 rounded-full bg-[#E2E8F0] flex items-center justify-center text-[#475569] text-xs font-bold">
              AD
            </div>
          </div>
        </header>

        {/* PAGE BODY */}
        <main className="p-8 space-y-6 max-w-7xl mx-auto w-full">
          {/* Toast Notification */}
          {toastMessage && (
            <div className="fixed bottom-6 right-6 z-50 bg-[#0F172A] text-white text-xs font-bold px-4 py-3 rounded-xl shadow-2xl flex items-center gap-2 animate-in slide-in-from-bottom-5 duration-200">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span>{toastMessage}</span>
            </div>
          )}

          {/* =============================================================== */}
          {/* TOP 3 SUMMARY CARDS (Exact match to screenshot)                */}
          {/* =============================================================== */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Card 1: Controls Requiring Attention */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-2xs">
              <span className="text-xs font-semibold text-[#64748B] block">
                Controls Requiring Attention
              </span>
              <div className="mt-2 text-4xl font-extrabold text-[#DC2626] tracking-tight">
                {controlsRequiringAttentionCount}
              </div>
              <div className="mt-3 flex items-center gap-1.5 text-xs text-[#64748B] font-medium">
                <AlertTriangle className="w-3.5 h-3.5 text-[#DC2626]" />
                <span>Action required</span>
              </div>
            </div>

            {/* Card 2: Missing Evidence */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-2xs">
              <span className="text-xs font-semibold text-[#64748B] block">
                Missing Evidence
              </span>
              <div className="mt-2 text-4xl font-extrabold text-[#DC2626] tracking-tight">
                {missingEvidenceCount}
              </div>
              <div className="mt-3 flex items-center gap-1.5 text-xs text-[#64748B] font-medium">
                <FileText className="w-3.5 h-3.5 text-[#64748B]" />
                <span>Awaiting upload</span>
              </div>
            </div>

            {/* Card 3: Frameworks Tracked */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-2xs">
              <span className="text-xs font-semibold text-[#64748B] block">
                Frameworks Tracked
              </span>
              <div className="mt-2 text-4xl font-extrabold text-[#0F172A] tracking-tight">
                {frameworksTrackedCount}
              </div>
              <div className="mt-3 flex items-center gap-1.5 text-xs text-[#64748B] font-medium">
                <Building2 className="w-3.5 h-3.5 text-[#64748B]" />
                <span>PCI-DSS, GDPR, PSD2, SOC 2</span>
              </div>
            </div>
          </div>

          {/* =============================================================== */}
          {/* MAIN CARD: Controls Requiring Attention (Exact match)           */}
          {/* =============================================================== */}
          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-2xs overflow-hidden">
            {/* Header with Title and Action Buttons */}
            <div className="p-6 border-b border-[#F1F5F9] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <h2 className="text-base font-bold text-[#0F172A] tracking-tight">
                Controls Requiring Attention
              </h2>

              <div className="flex items-center gap-3">
                {/* Filter Button */}
                <div className="relative">
                  <button
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    className="px-4 py-2 bg-white hover:bg-[#F8FAFC] text-[#334155] border border-[#CBD5E1] rounded-xl text-xs font-semibold flex items-center gap-2 shadow-2xs transition-colors cursor-pointer"
                  >
                    <Filter className="w-3.5 h-3.5 text-[#64748B]" />
                    <span>Filter</span>
                    {(selectedFrameworkFilter !== 'ALL' || selectedEvidenceFilter !== 'ALL') && (
                      <span className="w-2 h-2 rounded-full bg-[#0E7044]"></span>
                    )}
                  </button>

                  {/* Filter Dropdown */}
                  {showFilterDropdown && (
                    <div className="absolute right-0 mt-2 w-72 bg-white border border-[#E2E8F0] rounded-2xl shadow-xl p-4 z-30 space-y-3">
                      <div className="flex items-center justify-between pb-2 border-b border-[#F1F5F9]">
                        <span className="text-xs font-bold text-[#0F172A]">Filter Controls</span>
                        <button
                          onClick={() => {
                            setSelectedFrameworkFilter('ALL');
                            setSelectedEvidenceFilter('ALL');
                          }}
                          className="text-[11px] text-[#2563EB] hover:underline"
                        >
                          Reset
                        </button>
                      </div>

                      <div>
                        <label className="text-[11px] font-bold text-[#64748B] block mb-1">
                          Framework
                        </label>
                        <select
                          value={selectedFrameworkFilter}
                          onChange={(e) => setSelectedFrameworkFilter(e.target.value)}
                          className="w-full text-xs p-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl outline-none"
                        >
                          <option value="ALL">All Frameworks</option>
                          <option value="PCI-DSS">PCI-DSS v4.0</option>
                          <option value="GDPR">GDPR (EU)</option>
                          <option value="PSD2">PSD2 (EU)</option>
                          <option value="SOC 2">SOC 2 Type II</option>
                          <option value="ISO27001">ISO27001</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-[11px] font-bold text-[#64748B] block mb-1">
                          Evidence Status
                        </label>
                        <select
                          value={selectedEvidenceFilter}
                          onChange={(e) => setSelectedEvidenceFilter(e.target.value)}
                          className="w-full text-xs p-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl outline-none"
                        >
                          <option value="ALL">All Evidence Statuses</option>
                          <option value="Partial">Partial</option>
                          <option value="Missing">Missing</option>
                          <option value="Expired">Expired</option>
                          <option value="Complete">Complete</option>
                        </select>
                      </div>

                      <button
                        onClick={() => setShowFilterDropdown(false)}
                        className="w-full py-2 bg-[#0F4F4F] text-white rounded-xl text-xs font-bold"
                      >
                        Apply Filters
                      </button>
                    </div>
                  )}
                </div>

                {/* Export Report Button (Dark Teal styling from screenshot) */}
                <button
                  onClick={() => setShowExportModal(true)}
                  className="px-4 py-2 bg-[#0F4F4F] hover:bg-[#0A3D3D] text-white rounded-xl text-xs font-semibold flex items-center gap-2 shadow-xs transition-colors cursor-pointer"
                >
                  <Download className="w-3.5 h-3.5 text-white" />
                  <span>Export Report</span>
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-[#F1F5F9] text-[#64748B] font-semibold text-[11px] bg-[#F8FAFC]/50">
                    <th className="py-3.5 px-6">Control</th>
                    <th className="py-3.5 px-4">Jurisdiction / Framework</th>
                    <th className="py-3.5 px-4">Issue</th>
                    <th className="py-3.5 px-4">Owner</th>
                    <th className="py-3.5 px-4">Due</th>
                    <th className="py-3.5 px-4">Evidence</th>
                    <th className="py-3.5 px-6 text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {filteredControls.map((ctrl) => (
                    <tr
                      key={ctrl.id}
                      onClick={() => setSelectedControl(ctrl)}
                      className="hover:bg-[#F8FAFC] transition-colors cursor-pointer group"
                    >
                      {/* Control Code & Name */}
                      <td className="py-4 px-6 font-bold text-[#0F172A] whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span>{ctrl.code}</span>
                        </div>
                      </td>

                      {/* Jurisdiction / Framework */}
                      <td className="py-4 px-4 text-[#334155] whitespace-nowrap">
                        {ctrl.jurisdiction.includes('(Global)') ? (
                          <div>
                            <span className="font-semibold text-[#0F172A] block">PCI-DSS</span>
                            <span className="text-[11px] text-[#94A3B8] font-normal block">(Global)</span>
                          </div>
                        ) : (
                          <span className="font-semibold text-[#0F172A]">{ctrl.jurisdiction}</span>
                        )}
                      </td>

                      {/* Issue */}
                      <td className="py-4 px-4 text-[#475569] max-w-xs truncate" title={ctrl.issue}>
                        {ctrl.issue}
                      </td>

                      {/* Owner */}
                      <td className="py-4 px-4 text-[#334155] whitespace-nowrap font-medium">
                        {ctrl.owner}
                      </td>

                      {/* Due Date */}
                      <td className="py-4 px-4 text-[#64748B] whitespace-nowrap font-mono text-[11px]">
                        {ctrl.due}
                      </td>

                      {/* Evidence Pill */}
                      <td className="py-4 px-4 whitespace-nowrap">
                        {renderEvidenceBadge(ctrl.evidence)}
                      </td>

                      {/* Status Icon */}
                      <td className="py-4 px-6 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end">
                          {renderStatusIcon(ctrl.status, ctrl.evidence)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* =============================================================== */}
          {/* BOTTOM ROW (2 CARDS SIDE-BY-SIDE: Frameworks & Control Status)  */}
          {/* =============================================================== */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* ------------------------------------------------------------- */}
            {/* LEFT BOTTOM CARD: Framework Applicability                     */}
            {/* ------------------------------------------------------------- */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-2xs space-y-4">
              <h3 className="text-base font-bold text-[#0F172A] tracking-tight">
                Framework Applicability
              </h3>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-[#F1F5F9] text-[#64748B] font-semibold text-[11px]">
                      <th className="py-2.5 px-0">Framework</th>
                      <th className="py-2.5 px-4">Region</th>
                      <th className="py-2.5 px-4 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F1F5F9]">
                    {frameworks.map((fw) => (
                      <tr key={fw.id} className="hover:bg-[#F8FAFC]/60 transition-colors">
                        <td className="py-3.5 px-0 font-bold text-[#0F172A]">
                          {fw.framework}
                        </td>
                        <td className="py-3.5 px-4 text-[#475569] font-medium">
                          {fw.region}
                        </td>
                        <td className="py-3.5 px-4 text-right">
                          {renderApplicabilityStatus(fw.status)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ------------------------------------------------------------- */}
            {/* RIGHT BOTTOM CARD: Overall Control Status                     */}
            {/* ------------------------------------------------------------- */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-2xs space-y-5">
              <h3 className="text-base font-bold text-[#0F172A] tracking-tight">
                Overall Control Status
              </h3>

              <div className="space-y-4">
                {/* 1. Operating as Intended */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs font-semibold">
                    <span className="text-[#1E293B]">Operating as Intended</span>
                    <span className="font-bold text-[#0F172A]">{operatingCount}</span>
                  </div>
                  {/* Dark Teal / Forest Bar */}
                  <div className="w-full bg-[#E2E8F0] h-2.5 rounded-full overflow-hidden">
                    <div
                      className="bg-[#0F4F4F] h-full rounded-full transition-all duration-500"
                      style={{ width: `${operatingPercent}%` }}
                    ></div>
                  </div>
                </div>

                {/* 2. Needs Attention */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs font-semibold">
                    <span className="text-[#1E293B]">Needs Attention</span>
                    <span className="font-bold text-[#0F172A]">{needsAttentionCount}</span>
                  </div>
                  {/* Red Bar */}
                  <div className="w-full bg-[#E2E8F0] h-2.5 rounded-full overflow-hidden">
                    <div
                      className="bg-[#EF4444] h-full rounded-full transition-all duration-500"
                      style={{ width: `${needsAttentionPercent}%` }}
                    ></div>
                  </div>
                </div>

                {/* 3. Not Evaluated / Exempt */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs font-semibold">
                    <span className="text-[#1E293B]">Not Evaluated / Exempt</span>
                    <span className="font-bold text-[#0F172A]">{notEvaluatedCount}</span>
                  </div>
                  {/* Slate Bar */}
                  <div className="w-full bg-[#E2E8F0] h-2.5 rounded-full overflow-hidden">
                    <div
                      className="bg-[#334155] h-full rounded-full transition-all duration-500"
                      style={{ width: `${notEvaluatedPercent}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* =============================================================== */}
      {/* 3. CONTROL DETAIL & EVIDENCE INSPECTION DRAWER / MODAL          */}
      {/* =============================================================== */}
      {selectedControl && (
        <div className="fixed inset-0 bg-[#0F172A]/50 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl border border-[#E2E8F0] space-y-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between pb-4 border-b border-[#F1F5F9]">
              <div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-blue-50 text-[#2563EB] rounded text-[11px] font-mono font-bold">
                    {selectedControl.framework}
                  </span>
                  <span className="text-xs text-[#64748B] font-medium">{selectedControl.jurisdiction}</span>
                </div>
                <h3 className="text-lg font-bold text-[#0F172A] mt-1">
                  {selectedControl.code}
                </h3>
                <p className="text-xs text-[#64748B] mt-0.5">{selectedControl.title}</p>
              </div>
              <button
                onClick={() => setSelectedControl(null)}
                className="p-1 text-[#94A3B8] hover:text-[#0F172A] rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div>
                <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block mb-1">
                  Control Description & Scope
                </label>
                <p className="text-[#334155] leading-relaxed bg-[#F8FAFC] p-3 rounded-xl border border-[#E2E8F0]">
                  {selectedControl.description}
                </p>
              </div>

              <div>
                <label className="text-[11px] font-bold text-[#DC2626] uppercase tracking-wider block mb-1">
                  Identified Audit Finding / Issue
                </label>
                <div className="bg-red-50 text-[#991B1B] p-3 rounded-xl border border-red-100 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-[#DC2626] shrink-0 mt-0.5" />
                  <span>{selectedControl.issue}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#F8FAFC] p-3 rounded-xl border border-[#E2E8F0]">
                  <span className="text-[10px] font-bold text-[#64748B] uppercase block">Control Owner</span>
                  <span className="font-bold text-[#0F172A] text-xs mt-1 block">{selectedControl.owner}</span>
                  <span className="text-[10px] text-[#64748B]">{selectedControl.ownerEmail}</span>
                </div>

                <div className="bg-[#F8FAFC] p-3 rounded-xl border border-[#E2E8F0]">
                  <span className="text-[10px] font-bold text-[#64748B] uppercase block">Remediation Due Date</span>
                  <span className="font-mono font-bold text-[#DC2626] text-xs mt-1 block">{selectedControl.due}</span>
                  <span className="text-[10px] text-[#64748B]">Last Tested: {selectedControl.lastTestedDate}</span>
                </div>
              </div>

              {selectedControl.remediationPlan && (
                <div>
                  <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block mb-1">
                    Active Remediation Plan
                  </label>
                  <p className="text-[#334155] bg-emerald-50/50 p-3 rounded-xl border border-emerald-100">
                    {selectedControl.remediationPlan}
                  </p>
                </div>
              )}

              <div>
                <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block mb-1">
                  Current Evidence Status: {selectedControl.evidence} ({selectedControl.evidenceFilesCount} files attached)
                </label>
                <div className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                  <span className="text-xs text-[#64748B]">
                    {selectedControl.evidence === 'Complete'
                      ? 'All required audit artifacts and signed logs are uploaded.'
                      : 'Audit artifacts missing or pending validation.'}
                  </span>
                  <button
                    onClick={() => {
                      setUploadTargetControl(selectedControl);
                      setShowUploadEvidenceModal(true);
                      setSelectedControl(null);
                    }}
                    className="px-3 py-1.5 bg-[#0F4F4F] hover:bg-[#0A3D3D] text-white font-bold rounded-lg text-xs flex items-center gap-1.5 cursor-pointer"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    <span>Upload Evidence</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-[#F1F5F9]">
              <button
                onClick={() => setSelectedControl(null)}
                className="px-4 py-2 border border-[#CBD5E1] text-[#475569] font-bold rounded-xl text-xs hover:bg-[#F8FAFC]"
              >
                Close
              </button>
              <button
                onClick={() => {
                  triggerToast(`Control ${selectedControl.code} marked for priority auditor review.`);
                  setSelectedControl(null);
                }}
                className="px-4 py-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold rounded-xl text-xs cursor-pointer"
              >
                Request Auditor Re-test
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =============================================================== */}
      {/* 4. UPLOAD EVIDENCE MODAL                                        */}
      {/* =============================================================== */}
      {showUploadEvidenceModal && uploadTargetControl && (
        <div className="fixed inset-0 bg-[#0F172A]/50 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-[#E2E8F0] space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-[#F1F5F9]">
              <div>
                <h3 className="text-sm font-bold text-[#0F172A]">Upload Audit Evidence</h3>
                <p className="text-[11px] text-[#64748B]">{uploadTargetControl.code}</p>
              </div>
              <button
                onClick={() => setShowUploadEvidenceModal(false)}
                className="p-1 text-[#94A3B8] hover:text-[#0F172A] rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="border-2 border-dashed border-[#CBD5E1] rounded-2xl p-6 text-center hover:bg-[#F8FAFC] transition-colors cursor-pointer">
                <Upload className="w-8 h-8 text-[#64748B] mx-auto mb-2" />
                <span className="font-bold text-[#0F172A] block">Click to upload or drag & drop</span>
                <span className="text-[11px] text-[#94A3B8] block mt-1">
                  PDF, CSV, JSON, PNG (Max 25MB). Auto-calculated SHA-256 hash.
                </span>
              </div>

              <div>
                <label className="text-[11px] font-bold text-[#64748B] block mb-1">
                  Evidence Description / Period
                </label>
                <input
                  type="text"
                  placeholder="e.g. Q3 2026 DevOps SSH Bastion Access Sign-off Log"
                  className="w-full p-2.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs outline-none focus:bg-white focus:border-[#0F4F4F]"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-[#F1F5F9]">
              <button
                onClick={() => setShowUploadEvidenceModal(false)}
                className="px-3.5 py-2 border border-[#CBD5E1] text-[#475569] font-bold rounded-xl text-xs hover:bg-[#F8FAFC]"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  triggerToast(`Evidence successfully attached to ${uploadTargetControl.code}. Hash verified.`);
                  setShowUploadEvidenceModal(false);
                }}
                className="px-4 py-2 bg-[#0F4F4F] hover:bg-[#0A3D3D] text-white font-bold rounded-xl text-xs cursor-pointer"
              >
                Attach & Sign Evidence
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =============================================================== */}
      {/* 5. EXPORT REPORT MODAL                                          */}
      {/* =============================================================== */}
      {showExportModal && (
        <div className="fixed inset-0 bg-[#0F172A]/50 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-[#E2E8F0] space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-[#F1F5F9]">
              <div>
                <h3 className="text-sm font-bold text-[#0F172A]">Export Compliance Monitor Report</h3>
                <p className="text-[11px] text-[#64748B]">Generate auditor-ready status ledger & evidence matrix</p>
              </div>
              <button
                onClick={() => setShowExportModal(false)}
                className="p-1 text-[#94A3B8] hover:text-[#0F172A] rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <label className="text-[11px] font-bold text-[#64748B] block mb-1">
                  Report Scope
                </label>
                <select className="w-full p-2.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs outline-none">
                  <option>Full Platform Control Matrix (173 Controls)</option>
                  <option>Controls Requiring Attention Only (12 Controls)</option>
                  <option>PCI-DSS v4.0 Scoping & Evidence Ledger</option>
                  <option>GDPR & Article 30 Processing Records</option>
                </select>
              </div>

              <div>
                <label className="text-[11px] font-bold text-[#64748B] block mb-1">
                  Format
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <label className="p-2.5 border border-[#0F4F4F] bg-teal-50/50 rounded-xl text-center cursor-pointer">
                    <span className="font-bold text-[#0F4F4F] block">PDF Brief</span>
                    <span className="text-[10px] text-[#64748B]">Executive Audit</span>
                  </label>
                  <label className="p-2.5 border border-[#E2E8F0] rounded-xl text-center cursor-pointer hover:bg-[#F8FAFC]">
                    <span className="font-bold text-[#0F172A] block">CSV / Excel</span>
                    <span className="text-[10px] text-[#64748B]">Raw Evidence Matrix</span>
                  </label>
                  <label className="p-2.5 border border-[#E2E8F0] rounded-xl text-center cursor-pointer hover:bg-[#F8FAFC]">
                    <span className="font-bold text-[#0F172A] block">JSON Ledger</span>
                    <span className="text-[10px] text-[#64748B]">OSCAL Format</span>
                  </label>
                </div>
              </div>

              <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 text-amber-900 text-[11px] flex items-start gap-2">
                <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <span>
                  All exports are cryptographically signed with YamooPay Control Plane KMS Key and recorded to the immutable audit trail.
                </span>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-[#F1F5F9]">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-3.5 py-2 border border-[#CBD5E1] text-[#475569] font-bold rounded-xl text-xs hover:bg-[#F8FAFC]"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  triggerToast('Compliance report generated and downloaded successfully.');
                  setShowExportModal(false);
                }}
                className="px-4 py-2 bg-[#0F4F4F] hover:bg-[#0A3D3D] text-white font-bold rounded-xl text-xs flex items-center gap-1.5 cursor-pointer"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Download Report</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
